'use client';
import { Button } from 'antd';
import cn from 'classnames';
import { isFuture } from 'date-fns';
import Image from 'next/image';
import Link from 'next/link';
import React, { useMemo, useState } from 'react';

import { ReactComponent as SaleBadge } from '@/app/_icons/DemoSale_Banner.svg';
import { ReactComponent as EyeIcon } from '@/app/_icons/EyeIcon.svg';
import { ReactComponent as ImagePlaceholder } from '@/app/_icons/img-icon.svg';
import { ReactComponent as KonfidenceBadge } from '@/app/_icons/KonfidenceBadge.svg';
import { minDesktop } from '@/constants/vars.constants';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { getVehicleIdPath } from '@/hooks/used-cars/query-params.hook';
import { useVehicles } from '@/hooks/used-cars/vehicles.hook';
import { useWindowSize } from '@/hooks/window-size.hook';
import { DefaultButton } from '@/old-shared/components/buttons/default-button/default-button';
import { assetsUri } from '@/services/api.service';
import { getBase64ShimmerUrl } from '@/services/next.service';
import { numberWithSpacesToString } from '@/services/string-format.service';
import {
  CarTileTypes,
  Vehicle,
  VehicleType,
  ViewModes,
} from '@/types/used-cars/used-cars.types';
import { directusImageLoader } from '@/utils/loaders';

import { OldPrice } from '../old-price/old-price';
import { PriceFormat } from '../price-format/price-format';

import styles from './car-tile.module.scss';
import { useFiltersQuery } from '@/hooks/used-cars/filters-query.hook';

export const CarTile: React.FC<{
  details: Vehicle;
  type?: CarTileTypes;
  categoryLink?: string;
  price?: string;
  specialId?: string;
  sliderTile?: boolean;
  special?: boolean;
  view?: string;
}> = ({ details, sliderTile, special, view, price }) => {
  const [isHovered, setIsHovered] = useState(true);
  const { openLeadForm } = useFloatingCTA();
  const { vehiclesView } = useVehicles();
  const { activeCarType } = useFiltersQuery();

  const images =
    details.images &&
    details.images.length !== 0 &&
    Array.isArray(details.images)
      ? details.images?.map(
          (image) =>
            `${assetsUri}/${image.directus_files_id}?key=vehicle-thumbnail`,
        )
      : [];
  const imageUri = `${assetsUri}/${details.mainImage}?key=vehicle-thumbnail`;
  const isListMode = view
    ? vehiclesView === ViewModes.list && view !== 'tile'
    : vehiclesView === ViewModes.list;
  const isDemo = details.type === '3';
  const size = useWindowSize();
  const [vehicleImages, setVehicleImages] = useState([imageUri, ...images]);
  const title = details.title
    ? details.title
        .split(' ')
        .map((item) => {
          let itemLowerCase = item.toLowerCase();

          if (details.brand && details.brand.toLowerCase() === itemLowerCase) {
            return details.brand;
          }

          if (
            details.model &&
            details.model.toLowerCase().includes(itemLowerCase)
          ) {
            return (
              details.model
                .split(' ')
                .find((phrase) => phrase.toLowerCase() === itemLowerCase) ||
              item
            );
          }

          if (
            details.range_id &&
            details.range_id.toLowerCase().includes(itemLowerCase)
          ) {
            return (
              details.range_id
                .split(' ')
                .find((phrase) => phrase.toLowerCase() === itemLowerCase) ||
              item
            );
          }
          itemLowerCase = itemLowerCase
            .split('')
            .map((char, i, arr) => {
              if (char === '-') {
                const nextItem = arr[i + 1];

                if (nextItem) {
                  arr[i + 1] = nextItem.toUpperCase();
                }
              }

              return char;
            })
            .join('');

          return itemLowerCase
            ? itemLowerCase[0]?.toUpperCase() + itemLowerCase.slice(1)
            : itemLowerCase;
        })
        .join(' ')
    : '';

  const changeMainImage = (index: number) => {
    setVehicleImages(
      (prev) =>
        [
          prev[index],
          ...prev.slice(1, index),
          prev[0],
          ...prev.slice(index + 1),
        ] as string[],
    );
  };

  const hasNowPrice = useMemo(() => {
    const { nowPrice, price } = details;

    return nowPrice && +nowPrice > 0 && +nowPrice < +price;
  }, [details]);

  const isOnSale =
    details?.sale_end_date && isFuture(new Date(details.sale_end_date));

  return (
    <div
      className={cn(styles.carTileWrapper, {
        [styles.sliderTile as string]: sliderTile,
        [styles.carTileWrapperList as string]: isListMode,
        [styles.special as string]: special,
      })}
      data-cy="car-tile-link"
    >
      {isListMode ? (
        <Link href={getVehicleIdPath(details)} passHref>
          <div className={styles.carImageContainer}>
            {details.mainImage && (
              <div className={styles.imageWrapper}>
                <Image
                  src={vehicleImages[0] ? vehicleImages[0] : title}
                  loader={directusImageLoader}
                  alt={title}
                  className={styles.carImage}
                  width={400}
                  height={300}
                  placeholder="blur"
                  blurDataURL={getBase64ShimmerUrl(400, 300)}
                  priority={true}
                  loading="eager"
                />
                {(details?.is_konfidence || isDemo) && !isOnSale ? (
                  <>
                    <div
                      className={cn(styles.konfidenceBadge, styles.upper)}
                    ></div>
                    <KonfidenceBadge />
                    <div
                      className={cn(styles.konfidenceBadge, styles.lower)}
                    ></div>
                  </>
                ) : null}
                {isOnSale && (
                  <>
                    <div className={cn(styles.saleBadge, styles.upper)}></div>
                    <SaleBadge />
                    <div className={cn(styles.saleBadge, styles.lower)}></div>
                  </>
                )}
              </div>
            )}
            {details.imagesCount ? (
              <div className={cn(styles.carImageActions, 'car-tile-actions')}>
                <Button
                  className={cn(
                    styles.carImageAction,
                    styles.carImageActionImg,
                  )}
                  icon={<ImagePlaceholder className={styles.imageIcon} />}
                >
                  {details.imagesCount}
                </Button>
              </div>
            ) : null}
            <div
              className={cn({ [styles.viewDetails as string]: isHovered })}
              hidden={!isHovered}
            >
              <EyeIcon className={styles.viewDetailsImg} />
              <span className={styles.viewDetailsText}>View Details</span>
            </div>
          </div>
        </Link>
      ) : (
        <div className={styles.carImageContainer}>
          {details.mainImage && (
            <div className={styles.imageWrapper}>
              <Image
                //This is mobile
                src={vehicleImages[0] ? vehicleImages[0] : title}
                loader={directusImageLoader}
                alt={title}
                className={styles.carImage}
                width={400}
                height={300}
                placeholder="blur"
                blurDataURL={getBase64ShimmerUrl(400, 300)}
                priority={true}
                loading="eager"
                quality={30}
              />
              {(details?.is_konfidence || isDemo) && !isOnSale ? (
                <>
                  <div
                    className={cn(styles.konfidenceBadge, styles.upper)}
                  ></div>
                  <KonfidenceBadge />
                  <div
                    className={cn(styles.konfidenceBadge, styles.lower)}
                  ></div>
                </>
              ) : null}
              {isOnSale && (
                <>
                  <div className={cn(styles.saleBadge, styles.upper)}></div>
                  <SaleBadge />
                  <div className={cn(styles.saleBadge, styles.lower)}></div>
                </>
              )}
            </div>
          )}
          {details.imagesCount ? (
            <div className={cn(styles.carImageActions, 'car-tile-actions')}>
              <Button
                className={cn(styles.carImageAction, styles.carImageActionImg)}
                icon={<ImagePlaceholder className={styles.imageIcon} />}
              >
                {details.imagesCount}
              </Button>
            </div>
          ) : null}
          <Link
            className={cn({ [styles.viewDetails as string]: isHovered })}
            hidden={!isHovered}
            data-cy="car-image"
            href={getVehicleIdPath(details)}
          >
            <EyeIcon className={styles.viewDetailsImg} />
            <span className={styles.viewDetailsText}>View Details</span>
          </Link>
        </div>
      )}
      {isListMode && (
        <div className={styles.carInfoAdditionalImages}>
          {[1, 2, 3].map((index) => (
            <div className={styles.carInfoAdditionalImagesRow} key={index}>
              {vehicleImages[2 * index + 1] ? (
                <Image
                  src={vehicleImages[2 * index + 1] || ''}
                  loader={directusImageLoader}
                  alt="Aditional car"
                  className={styles.carInfoAdditionalImageImg}
                  onClick={() => changeMainImage(2 * index + 1)}
                  width={400}
                  height={300}
                  placeholder="blur"
                  blurDataURL={getBase64ShimmerUrl(400, 300)}
                />
              ) : (
                <div className={styles.carInfoAdditionalImage}></div>
              )}
              {vehicleImages[2 * index + 2] ? (
                <Image
                  src={vehicleImages[2 * index + 2] || ''}
                  loader={directusImageLoader}
                  alt="Aditional car"
                  className={styles.carInfoAdditionalImageImg}
                  onClick={() => changeMainImage(2 * index + 2)}
                  width={400}
                  height={300}
                  placeholder="blur"
                  blurDataURL={getBase64ShimmerUrl(400, 300)}
                />
              ) : (
                <div className={styles.carInfoAdditionalImage}></div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className={styles.carInfoWrapper}>
        {size.width && size.width < minDesktop ? (
          <div className={styles.carInfoHeader}>
            <Link href={getVehicleIdPath(details)}>
              <div className={styles.carInfoBlock}>
                <span className={styles.carInfoName}>{title}</span>
                <span className={styles.carInfoHeaderStat}>
                  {details.model}
                </span>
              </div>
              <div className={styles.carInfoBlock}>
                <span className={styles.carInfoPrice}>
                  <PriceFormat
                    value={
                      !special && +details?.nowPrice <= 0
                        ? details.price
                        : !special && +details?.nowPrice > 0
                          ? details.nowPrice
                          : price
                    }
                    thousandSeparator=","
                    prefix="R"
                  />
                </span>
                {special ? (
                  <OldPrice
                    price={details.price}
                    className={styles.carInfoHeaderStat}
                  />
                ) : (
                  <>
                    {!special && hasNowPrice && (
                      <OldPrice
                        price={details.price}
                        className={styles.carInfoHeaderStat}
                      />
                    )}
                    {!special && !hasNowPrice && (
                      <span className={styles.carInfoHeaderStat}>
                        R{details.monthlyRepayment} p/m
                      </span>
                    )}
                  </>
                )}
              </div>
              <div
                className={!special && !hasNowPrice ? styles.hideElement : ''}
              >
                <span className={styles.carInfoHeaderStat}>
                  R{details.monthlyRepayment} p/m
                </span>
              </div>
            </Link>
          </div>
        ) : (
          <div className={styles.carInfoHeader}>
            <Link href={getVehicleIdPath(details)}>
              <div className={styles.carInfoBlock}>
                <h2 className={styles.carInfoName}>{title}</h2>
                <span className={styles.carInfoPrice}>
                  <PriceFormat
                    value={
                      !special && +details?.nowPrice <= 0
                        ? details.price
                        : !special && +details?.nowPrice > 0
                          ? details.nowPrice
                          : price
                    }
                    thousandSeparator=","
                    prefix="R"
                  />
                </span>
              </div>
              <div className={styles.carInfoBlock}>
                <span className={styles.carInfoHeaderStat}>
                  {details.model}
                </span>
                {special ? (
                  <OldPrice
                    price={details.price}
                    className={styles.carInfoHeaderStat}
                  />
                ) : (
                  <>
                    {!special && hasNowPrice && (
                      <OldPrice
                        price={details.price}
                        className={styles.carInfoHeaderStat}
                      />
                    )}
                    {!special && !hasNowPrice && (
                      <span className={styles.carInfoHeaderStat}>
                        R{details.monthlyRepayment} p/m
                      </span>
                    )}
                  </>
                )}
              </div>
              <div
                className={
                  !special && hasNowPrice
                    ? styles.carInfoNowPriceBlock
                    : styles.hideElement
                }
              >
                <span className={styles.carInfoHeaderStat}>
                  R{details.monthlyRepayment} p/m
                </span>
              </div>
            </Link>
          </div>
        )}

        <div className={styles.carInfoStatsWrap}>
          <div className={styles.carInfoStats}>
            <div className={styles.carInfoStatsColumn}>
              <div className={styles.carInfoStatsNames}>
                <div>Mileage</div>
              </div>
              <div className={styles.carInfoStatDetails}>
                <div>{numberWithSpacesToString(details.mileage, ' ')}</div>
              </div>
            </div>
            <div className={styles.carInfoStatsColumn}>
              <div className={styles.carInfoStatsNames}>
                <div>Transmission</div>
              </div>
              <div className={styles.carInfoStatDetails}>
                <div>{details.transmission}</div>
              </div>
            </div>
            <div className={styles.carInfoStatsColumn}>
              <div className={styles.carInfoStatsNames}>
                <div>Body Type</div>
              </div>
              <div className={styles.carInfoStatDetails}>
                <div>{details.bodyType}</div>
              </div>
            </div>
          </div>

          <div className={styles.carInfoStats}>
            <div className={styles.carInfoStatsColumn}>
              <div className={styles.carInfoStatsNames}>
                <div>Fuel type</div>
              </div>
              <div className={styles.carInfoStatDetails}>
                <div>
                  {details.fuelType === 'Petrol' ||
                  details.fuelType === 'Diesel'
                    ? details.fuelType
                    : 'Hybrid'}
                </div>
              </div>
            </div>

            <div className={styles.carInfoStatsColumn}>
              <div className={styles.carInfoStatsNames}>
                <div>Doors</div>
              </div>
              <div className={styles.carInfoStatDetails}>
                <div>{details.doors}</div>
              </div>
            </div>

            <div className={styles.carInfoStatsColumn}>
              <div className={styles.carInfoStatsNames}>
                <div>Drivetrain</div>
              </div>
              <div className={styles.carInfoStatDetails}>
                <div>{details.drivetrain}</div>
              </div>
            </div>
          </div>
        </div>

        <div className={cn(styles.carInfoFooter, styles.carInfoBlock)}>
          <div className={styles.carInfoLocation}>
            {details?.dealership_id?.name}
          </div>
          <div className={styles.carInfoButtons}>
            {isListMode && (
              <Link href={getVehicleIdPath(details)} passHref>
                <div className={styles.detailsButton}>
                  <DefaultButton label="View Details" light />
                </div>
              </Link>
            )}
            <div
              className={styles.enquireButton}
              onMouseEnter={() => {
                setIsHovered(false);
              }}
              onMouseLeave={() => {
                setIsHovered(true);
              }}
            >
               {activeCarType !== VehicleType.Demo && <DefaultButton
                label="Enquire Now"
                onClick={(e) => {
                  e.stopPropagation();
                  openLeadForm('used-cars', 'UsedCars', {
                    type: 'used-car',
                    usedCar: details,
                  });
                  e.preventDefault();
                }}
                data-cy="enquire-now-button"
              />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
